import React from "react";
import { Card, Flex, Text, LoadingIndicator, Icon } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { DetailedPayeeLogFragment } from "../../../../graphql/supergraph.index";
import { PayeeStatus, EventType } from "../../../../graphql/supergraphOperations";
import * as styles from "./PayeeLogsList.css";

interface PayeeLogsListProps {
  logs: DetailedPayeeLogFragment[];
  loading: boolean;
  error?: Error | null;
}

export const PayeeLogsList: React.FC<PayeeLogsListProps> = ({
  logs,
  loading,
  error: loadingError,
}) => {
  const getLogIcon = (logEntry: DetailedPayeeLogFragment): "check-circle" | "times-circle" => {
    // Use check-circle for approved/created actions, times-circle for rejected actions
    if (logEntry.status === PayeeStatus.Rejected) {
      return "times-circle";
    }
    return "check-circle";
  };

  const formatActionDescription = (logEntry: DetailedPayeeLogFragment): string => {
    const userName = logEntry.user?.fullName || "Unknown User";

    switch (logEntry.eventType) {
      case EventType.Create:
        return `Payee created and submitted for review by ${userName}`;
      case EventType.Update:
        if (logEntry.status === PayeeStatus.Approved) {
          return `Payee approved by ${userName}`;
        } else if (logEntry.status === PayeeStatus.Rejected) {
          return `Reviewed and returned by ${userName}`;
        } else if (
          (logEntry.previousStatus === PayeeStatus.Rejected ||
            logEntry.previousStatus === PayeeStatus.Approved) &&
          logEntry.status === PayeeStatus.Pending
        ) {
          return `Payee edited and submitted for review by ${userName}`;
        }
        return `Payee updated by ${userName}`;
      default:
        return `Action performed by ${userName}`;
    }
  };

  const formatDateTime = (dateTime: string): string => {
    try {
      const date = new Date(dateTime);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return dateTime;
    }
  };

  if (loading) {
    return (
      <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={16}>
        <LoadingIndicator />
        <Text variant="regular-md" color="muted">
          Loading activity logs...
        </Text>
      </Flex>
    );
  }

  if (loadingError) {
    return (
      <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={16}>
        <Text variant="regular-md" color="error">
          Error loading activity logs: {loadingError.message}
        </Text>
      </Flex>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={16}>
        <Text variant="regular-md" color="muted">
          No activity logs found
        </Text>
      </Flex>
    );
  }

  return (
    <Card padding="lg" title="Log">
      <Flex flexDirection="column" gap={16}>
        {logs.map((logEntry) => {
          return (
            <Flex key={logEntry.id} flexDirection="column" gap={8}>
              <Flex alignItems="flex-start" gap={12}>
                <div className={styles.iconContainer}>
                  <Icon
                    type={getLogIcon(logEntry)}
                    size="medium"
                    customColor={Colors.Neutral900}
                  />
                </div>
                <Flex flexDirection="column" gap={4} className={styles.logEntryContent}>
                  <Text variant="regular-md">{formatActionDescription(logEntry)}</Text>
                  <Text variant="secondary-sm" color="muted">
                    {/* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */}
                    {formatDateTime(logEntry.dateTime)}
                  </Text>
                  {logEntry.status === PayeeStatus.Rejected && logEntry.reason && (
                    <div className={styles.rejectionReasonBox}>
                      <Text variant="regular-sm">{logEntry.reason}</Text>
                    </div>
                  )}
                </Flex>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Card>
  );
};

export default PayeeLogsList;
